// toastService.ts
import { reactive } from 'vue'
import { i18n } from '../modules/i18n'

interface ToastOptions {
  message?: string
  duration?: number
  type?: 'info' | 'success' | 'warning' | 'error'
  icon?: () => unknown
  error?: Error
  variables?: Record<string, string | number>
}

interface Toast {
  id: number
  title: string
  message: string
  type: string
  duration: number
  icon?: () => unknown
  progress: number
}

const toasts: Toast[] = reactive([])

function removeToast(id: number) {
  const index = toasts.findIndex(toast => toast.id === id)
  if (index > -1) {
    toasts.splice(index, 1)
  }
}

// Create the instance with the translation function
export function addToast(title: string, options: ToastOptions = {}) {
  const id = Date.now()
  const duration = options.duration || 5000
  const toast: Toast = reactive({
    id,
    title,
    message: options.message || '',
    type: options.type || 'info',
    duration,
    icon: options.icon,
    progress: 100,
  })

  // Helper function to translate with variables
  const translateWithVariables = (key: string, variables?: Record<string, string | number>) => {
    if (variables) {
      return i18n.global.t(key, variables)
    }
    return i18n.global.t(key)
  }

  // Handle error object if provided
  if (options.error) {
    const errorMessage = Array.isArray(options.error) ? options.error[0].message : options.error.message
    if (errorMessage.startsWith('$t.')) {
      // Check if the error message contains variables (format: $t.key|{"var1": "value1"})
      const [translationKeyWithPrefix, variablesJson] = errorMessage.split('|')
      const translationKey = translationKeyWithPrefix.substring(3) // Remove the $t. prefix

      let variables = options.variables
      if (variablesJson) {
        try {
          const parsedVariables = JSON.parse(variablesJson)
          variables = { ...variables, ...parsedVariables }
        } catch (e) {
          console.error(e)
          // If JSON parsing fails, just use the original variables
        }
      }

      const translatedMessage = translateWithVariables(`backend_keys.${translationKey}`, variables)
      // Only use the translation if it exists and is different from the key
      if (translatedMessage !== `backend_keys.${translationKey}`) {
        toast.title = translatedMessage
        toast.message = options.message || i18n.global.t('errors.try_again_later')
      }
      else {
        toast.title = i18n.global.t('errors.try_again_later')
      }
    }
    else {
      toast.title = toast.title || i18n.global.t('errors.try_again_later')
    }
    toast.type = 'error'
  }
  else if (toast.type === 'error') {
    toast.message = toast.message || i18n.global.t('errors.try_again_later')
  }

  // Handle title translation with variables
  if (title.startsWith('$t.')) {
    const translationKey = title.substring(3) // Remove the $t. prefix
    const translatedTitle = translateWithVariables(translationKey, options.variables)
    // Only use the translation if it exists and is different from the key
    if (translatedTitle !== translationKey) {
      toast.title = translatedTitle
    }
  }

  // Handle message translation with variables
  if (options.message && options.message.startsWith('$t.')) {
    const translationKey = options.message.substring(3) // Remove the $t. prefix
    const translatedMessage = translateWithVariables(translationKey, options.variables)
    // Only use the translation if it exists and is different from the key
    if (translatedMessage !== translationKey) {
      toast.message = translatedMessage
    }
  }

  toasts.push(toast)

  const startTime = Date.now()
  const updateProgress = () => {
    const elapsedTime = Date.now() - startTime
    const progress = 100 - (elapsedTime / duration) * 100
    if (progress <= 0) {
      removeToast(id)
    }
    else {
      toast.progress = progress
      requestAnimationFrame(updateProgress)
    }
  }
  requestAnimationFrame(updateProgress)
}

export { removeToast, toasts }
